import i18n from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Documentation: https://react.i18next.com

const defaultLng = "en-US";
const initLng = window.localStorage.getItem("i18nextLng") || defaultLng;

export const nameSpaces = {
  translation: "translation",
  relayYardControl: "relayYardControl",
  relayYardTracker: "relayYardTracker",
  ownerConfiguration: "ownerConfiguration",
  ryteTracker: "ryteTracker",
  hostlerPool: "hostlerPool",
  agentStudio: "agentStudio",
};

// Modified to use webpack's dynamic import with explicit chunk naming
const translationImports = {
  [nameSpaces.translation]: (lng) =>
    import(
      /* webpackChunkName: "i18n-[request]" */
      `../../translations/i18n/strings-${lng}.puff.json`
    ),
  [nameSpaces.relayYardControl]: (lng) =>
    import(
      /* webpackChunkName: "i18n-ryc-[request]" */
      `../../translations/pages/relayYardControl/i18n/strings-${lng}.puff.json`
    ),
  [nameSpaces.relayYardTracker]: (lng) =>
    import(
      /* webpackChunkName: "i18n-ryt-[request]" */
      `@amzn/ytyard360-relay-yard-tracker/translations/i18n/strings-${lng}.puff.json`
    ),
  [nameSpaces.ownerConfiguration]: (lng) =>
    import(
      /* webpackChunkName: "i18n-owner-config-[request]" */
      `@amzn/ytyard360-owner-config/translations/i18n/strings-${lng}.puff.json`
    ),
  [nameSpaces.ryteTracker]: (lng) =>
    import(
      /* webpackChunkName: "i18n-ryte-[request]" */
      `../../translations/pages/ryteTracker/i18n/strings-${lng}.puff.json`
    ),
  [nameSpaces.hostlerPool]: (lng) =>
    import(
      /* webpackChunkName: "i18n-hostler-pool-[request]" */
      `@amzn/ytyard360-hostler-pool/translations/i18n/strings-${lng}.puff.json`
    ),
  [nameSpaces.agentStudio]: (lng) =>
    import(
      /* webpackChunkName: "i18n-agent-studio-[request]" */
      `@amzn/ytyard360-agent-studio/translations/i18n/strings-${lng}.puff.json`
    ),
};

const getTranslationImport = (namespace, lng) => {
  return translationImports[namespace]
    ? translationImports[namespace](lng)
    : translationImports.translation(lng);
};

const flattenPuffResources = (resources) =>
  Object.fromEntries(
    Object.entries(resources ?? {}).map(([key, entry]) => [
      key,
      entry && typeof entry === "object" && typeof entry.value === "string" ? entry.value : entry,
    ])
  );

// Add webpack context to ensure files are included in the build
const requireTranslations = require.context("../../translations", true, /strings-.*\.puff\.json$/);

i18n
  .use(
    resourcesToBackend((lng, namespace, callback) => {
      getTranslationImport(namespace, lng)
        .then(({ resources }) => {
          callback(null, flattenPuffResources(resources));
        })
        .catch((error) => {
          // Fallback to synchronously loaded translations if dynamic import fails
          try {
            const key = requireTranslations
              .keys()
              .find((k) => k.includes(lng) && k.includes(namespace));
            if (key) {
              callback(null, flattenPuffResources(requireTranslations(key).resources));
            } else {
              callback(error, null);
            }
          } catch (e) {
            callback(error, null);
          }
        });
    })
  )
  // Store user language selection in browser
  .use(LanguageDetector)
  // Pass i18n down to react-i18next
  .use(initReactI18next)
  .init({
    lng: initLng,
    fallbackLng: "en-US",
    load: "currentOnly",
    debug: process.env.NODE_ENV === "development",
    interpolation: {
      escapeValue: false, // something to do with xss, actually not needed for react
    },
    ns: Object.keys(nameSpaces),
    // Add partial bundling support
    partialBundledLanguages: true,
  });

export default i18n;
